/**
 * td-product-set-builder
 *
 * Drives the "Pairs well with" body of the PDP recommendations block
 * (snippets/td-product-recommendations__body.liquid). Each row has its own "Add" button that
 * puts that item straight into the cart:
 *
 *  - keeps every row's size select in step with the size chosen in the buy box
 *  - refuses to add a row with no size chosen, and says so on that row
 *  - shows a row as "Added" once its product is in the cart, from any source
 *
 * All user-facing copy arrives on data-* attributes or in the markup; this file holds none.
 */
if (!customElements.get('td-product-set-builder')) {
  class TdProductSetBuilder extends HTMLElement {
    constructor() {
      super();
      // Bound once so the same references can be added and removed across reconnects.
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
    }

    connectedCallback() {
      // Listeners on document are removed in disconnectedCallback, so they have to come back
      // on every connect -- including a re-insert, which skips the one-time setup below.
      document.addEventListener('tdVariantChange', this.onVariantChange);
      // Broadcast by assets/td-cart-a11y.js after any cart write, from the theme or an app.
      document.addEventListener('td:cart:updated', this.onCartUpdated);

      // Everything else lives on this element and survives a re-insert; set it up once.
      if (this.initialised) return;
      this.initialised = true;

      this.rows = Array.from(this.querySelectorAll('[data-td-set-row]'));
      this.statusElement = this.querySelector('[data-td-set-status]');
      this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
      this.currentSize = this.dataset.currentSize || '';

      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('tdVariantChange', this.onVariantChange);
      document.removeEventListener('td:cart:updated', this.onCartUpdated);
    }

    /* ----------------------------------------------------------------------
       Cart contents
    ---------------------------------------------------------------------- */

    onCartUpdated(event) {
      const cart = event.detail && event.detail.cart;
      if (!cart || !Array.isArray(cart.items)) return;

      const productIds = new Set(cart.items.map((item) => String(item.product_id)));
      this.rows.forEach((row) => this.setRowInCart(row, productIds.has(row.dataset.tdSetProductId)));
    }

    /**
     * The row class picks the "Add" or "Added" label in CSS. aria-disabled rather than
     * disabled on the button, so focus stays on the button the shopper just pressed. The size
     * select locks too: changing size next to an "Added" button that does nothing would
     * mislead.
     */
    setRowInCart(row, inCart) {
      row.classList.toggle('td-set-builder__row--in-cart', inCart);

      const select = row.querySelector('select[data-td-set-variant-input]');
      if (select) select.disabled = inCart;

      const button = row.querySelector('[data-td-set-add]');
      if (!button) return;

      if (inCart) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    /* ----------------------------------------------------------------------
       Buy-box size
    ---------------------------------------------------------------------- */

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      const hasSizeOption = this.sizeOptionIndex >= 0;

      this.currentSize =
        variant && hasSizeOption && variant.options ? variant.options[this.sizeOptionIndex] || '' : '';

      this.matchRecommendationSizes();
    }

    /**
     * Move every row's select onto the shopper's size where that size is in stock. Rows that
     * cannot match go back to the empty placeholder and say so, rather than quietly holding a
     * different size that could be added to the cart. A size the shopper picked in the row
     * themselves is left alone.
     */
    matchRecommendationSizes() {
      if (!this.currentSize) return;

      this.rows.forEach((row) => {
        if (row.dataset.tdSetUserPicked) return;

        const select = row.querySelector('select[data-td-set-variant-input]');
        const sizeIndex = parseInt(row.dataset.tdSetSizeIndex, 10);
        if (!select || !(sizeIndex >= 0)) return;

        const match = Array.from(select.options).find(
          (option) => option.value && !option.disabled && option.dataset.size === this.currentSize
        );

        select.value = match ? match.value : '';
        this.clearRowError(row);

        const note = row.querySelector('[data-td-set-note]');
        if (note) note.hidden = Boolean(match);
      });
    }

    /* ----------------------------------------------------------------------
       Events
    ---------------------------------------------------------------------- */

    onChange(event) {
      const select = event.target.closest('select[data-td-set-variant-input]');
      if (!select || !select.value) return;

      // A size chosen in the row is the shopper's own call: stop matching it to the buy box.
      const row = select.closest('[data-td-set-row]');
      row.dataset.tdSetUserPicked = 'true';
      this.clearRowError(row);

      const note = row.querySelector('[data-td-set-note]');
      if (note) note.hidden = true;
    }

    onClick(event) {
      const button = event.target.closest('[data-td-set-add]');
      if (!button) return;

      const row = button.closest('[data-td-set-row]');
      if (row) this.addRow(row, button);
    }

    /* ----------------------------------------------------------------------
       Cart
    ---------------------------------------------------------------------- */

    rowVariantId(row) {
      const input = row.querySelector('[data-td-set-variant-input]');
      return input && input.value ? input.value : '';
    }

    addRow(row, button) {
      // aria-disabled covers both "already added" and "adding now". It does not stop Enter or
      // Space by itself, so it is checked here; this is the double-add guard.
      if (button.getAttribute('aria-disabled') === 'true') return;

      const variantId = this.rowVariantId(row);
      if (!variantId) {
        const select = row.querySelector('select[data-td-set-variant-input]');
        this.showRowError(row, select ? select.dataset.tdSetMissing : this.dataset.errorGeneric);
        if (select) {
          select.setAttribute('aria-invalid', 'true');
          select.focus();
        }
        return;
      }

      this.clearRowError(row);
      this.setLoading(button, true);

      fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
        .then(({ ok, body }) => {
          this.setLoading(button, false);

          // A 422 (sold out, or quantity unavailable) comes back with a description.
          if (!ok || body.status) {
            this.showRowError(row, body.description || body.message || this.dataset.errorGeneric);
            return;
          }

          // Flip the row now rather than waiting on td:cart:updated, which arrives after a
          // debounce -- the button must not become pressable again in between.
          this.setRowInCart(row, true);
          this.announce(this.dataset.statusAdded);
          this.refreshCart();
        })
        .catch(() => {
          this.setLoading(button, false);
          this.showRowError(row, this.dataset.errorGeneric);
        });
    }

    /**
     * The cart drawer (UpCart) and assets/td-cart-a11y.js both watch fetches to
     * /cart/add.js and refresh themselves. This re-reads the cart for the theme's own
     * mini-cart markup, which has no such hook.
     */
    refreshCart() {
      if (typeof window.refreshCart !== 'function') return;

      fetch(`${window.routes ? window.routes.cart_url : '/cart'}.js`)
        .then((response) => response.json())
        .then((cart) => window.refreshCart(cart))
        .catch(() => {});
    }

    /* ----------------------------------------------------------------------
       Feedback
    ---------------------------------------------------------------------- */

    setLoading(button, isLoading) {
      button.classList.toggle('td-set-builder__button--loading', isLoading);
      if (isLoading) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    /** The error takes over the row's message line from the size note while it shows. */
    showRowError(row, message) {
      const error = row.querySelector('[data-td-set-error]');
      if (!error || !message) return;

      error.textContent = message;
      row.classList.add('td-set-builder__row--error');
    }

    clearRowError(row) {
      const error = row.querySelector('[data-td-set-error]');
      if (error) error.textContent = '';
      row.classList.remove('td-set-builder__row--error');

      const select = row.querySelector('select[data-td-set-variant-input]');
      if (select) select.removeAttribute('aria-invalid');
    }

    /**
     * Live regions only speak when their text changes, so a second "Added to cart" would be
     * silent. Empty the region first, then write the message after a short beat. A timeout
     * rather than requestAnimationFrame, which is paused in a background tab and would drop
     * the message for a shopper who switched tabs mid-add.
     */
    announce(message) {
      if (!this.statusElement || !message) return;
      window.clearTimeout(this.announceTimer);
      this.statusElement.textContent = '';
      this.announceTimer = window.setTimeout(() => {
        this.statusElement.textContent = message;
      }, 100);
    }
  }

  customElements.define('td-product-set-builder', TdProductSetBuilder);
}
